/**
 * useFCM — Custom React hook to manage Firebase Cloud Messaging (FCM) web push tokens.
 *
 * What it does:
 * 1. Requests browser notification permission from the user.
 * 2. Registers the firebase-messaging-sw.js service worker.
 * 3. Gets the FCM device token using the VAPID key.
 * 4. Saves the token to the backend (PostgreSQL via Prisma) for this user.
 * 5. Handles token refresh (onTokenRefresh is deprecated in v9+, so we just re-register on login).
 * 6. Clears the token from backend on logout.
 *
 * Usage:
 *   import { useFCM } from '../hooks/useFCM';
 *
 *   // In App.jsx or after login:
 *   const { initFCM, clearFCM } = useFCM(currentUser);
 *   useEffect(() => { if (currentUser) initFCM(); }, [currentUser]);
 */

import { useState, useCallback, useRef } from 'react';
import { getToken } from 'firebase/messaging';
import { getFirebaseMessaging, VAPID_KEY } from '../lib/firebaseMessaging';

const API_BASE_URL =
    import.meta.env.VITE_API_URL ||
    (['localhost', '127.0.0.1'].includes(window.location.hostname)
        ? 'http://localhost:3059/api'
        : 'https://crmaaafm.inplsoftwares.com/api');

/**
 * Saves the FCM token to the backend database for the given user.
 * @param {number} userId
 * @param {string} fcmToken
 */
async function saveFcmTokenToBackend(userId, fcmToken) {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/users/fcm-token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ userId, fcmToken }),
    });
    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save FCM token');
    }
    return response.json();
}

/**
 * Clears the FCM token from the backend database (call on logout).
 * @param {number} userId
 */
async function clearFcmTokenFromBackend(userId) {
    const token = localStorage.getItem('token');
    await fetch(`${API_BASE_URL}/users/fcm-token`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ userId }),
    });
}

/**
 * @param {Object|null} currentUser - The logged-in user object (needs .uid or .id)
 */
export function useFCM(currentUser) {
    const [fcmStatus, setFcmStatus] = useState('idle'); // 'idle' | 'pending' | 'granted' | 'denied' | 'error'
    const [fcmToken, setFcmToken] = useState(null);
    const initializedRef = useRef(false);

    /**
     * initFCM()
     * Call this once after the user logs in (or on app load if already logged in).
     * It is idempotent — calling it multiple times is safe.
     */
    const initFCM = useCallback(async () => {
        // Guard: only run once per session
        if (initializedRef.current) return;

        const userId = currentUser?.uid || currentUser?.id;
        if (!userId) {
            console.warn('[FCM] initFCM called but no user is logged in. Skipping.');
            return;
        }

        // 1. Check browser support
        if (!('serviceWorker' in navigator) || !('Notification' in window)) {
            console.warn('[FCM] Push notifications not supported in this browser.');
            setFcmStatus('error');
            return;
        }

        setFcmStatus('pending');

        try {
            // 2. Request permission
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                console.warn('[FCM] Notification permission denied by user.');
                setFcmStatus('denied');
                return;
            }

            // 3. Register the Firebase messaging service worker
            //    (must be at the root of your public folder)
            const swRegistration = await navigator.serviceWorker.register(
                '/firebase-messaging-sw.js',
                { scope: '/' }
            );
            await navigator.serviceWorker.ready;
            console.log('[FCM] Service worker registered:', swRegistration.scope);

            // 4. Get the FCM messaging instance
            const messaging = await getFirebaseMessaging();
            if (!messaging) {
                setFcmStatus('error');
                return;
            }

            // 5. Get the FCM token
            const token = await getToken(messaging, {
                vapidKey: VAPID_KEY,
                serviceWorkerRegistration: swRegistration,
            });

            if (!token) {
                console.warn('[FCM] No token received. The user may have blocked notifications.');
                setFcmStatus('denied');
                return;
            }

            console.log('[FCM] Token obtained:', token.substring(0, 20) + '...');

            // 6. Save token to backend
            await saveFcmTokenToBackend(userId, token);
            console.log('[FCM] ✅ Token saved to backend for userId:', userId);

            setFcmToken(token);
            setFcmStatus('granted');
            initializedRef.current = true;

        } catch (err) {
            console.error('[FCM] Error during FCM initialization:', err);
            setFcmStatus('error');
        }
    }, [currentUser]);

    /**
     * clearFCM()
     * Call this on logout to remove the stale FCM token from the database.
     * This prevents sending push notifications to signed-out devices.
     */
    const clearFCM = useCallback(async () => {
        const userId = currentUser?.uid || currentUser?.id;
        if (!userId) return;

        try {
            await clearFcmTokenFromBackend(userId);
            console.log('[FCM] Token cleared from backend for userId:', userId);
        } catch (err) {
            console.warn('[FCM] Could not clear FCM token:', err.message);
        } finally {
            setFcmToken(null);
            setFcmStatus('idle');
            initializedRef.current = false;
        }
    }, [currentUser]);

    return {
        initFCM,
        clearFCM,
        fcmToken,
        fcmStatus,
        isGranted: fcmStatus === 'granted',
        isDenied: fcmStatus === 'denied',
    };
}

export default useFCM;
